// libraries
import React from 'react';
import styled from 'styled-components';

// styled components
import { H3, Eyebrow, Title } from 'shared/typography';
import { Flex, ColumnedFlex } from 'shared/styledComponents';

// assets
import devices from 'theme/devices';

const Wrapper = styled(ColumnedFlex)`
  width: 100%;
  outline: none;
`;
const UserConnectionPoints = styled(ColumnedFlex)`
  width: 100%;
  padding: 40px 40px 0 40px;
`;

const UserConnectionPoint = styled(ColumnedFlex)`
  border-bottom: 1px solid ${({ theme: { colors } }): string => colors.lightGray};
  margin-bottom: 30px;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
`;
const UserConnectionPointTitle = styled(Eyebrow)`
  margin-bottom: 10px;
`;

const UserConnectionPointSubtitle = styled(Title)`
  word-wrap: break-word;
`;

const ProviderBlockWrapper = styled(ColumnedFlex)`
  width: 100%;
  min-height: 315px;
  background: ${({ theme: { colors } }): string => colors.secondary};
`;

const ProviderBlock = styled(ColumnedFlex)`
  padding: 40px;

  @media ${devices.mobile} {
    padding: 40px 15px 35px 35px;
  }
`;

const ProviderBlockTitle = styled(Eyebrow)`
  margin-bottom: 40px;
`;

const ImageWrapper = styled.div<any>`
  width: 110px;
  height: 110px;
  background-image: ${({ src }): string => `url(${src})`};
  background-position: center;
  background-size: cover;
  background-position-y: 0;
  background-repeat: no-repeat;
  border-radius: 50%;
  margin-right: 20px;
  cursor: pointer;

  @media ${devices.mobile} {
    width: 75px;
    height: 75px;
  }
`;

const ProviderName = styled(H3)`
  margin-top: 15px;
  margin-bottom: 10px;
  cursor: pointer;
`;

const ServicesWrapper = styled(Flex)`
  flex-wrap: wrap;
  margin-bottom: 30px;
`;

const SpecializationsWrapper = styled(ServicesWrapper)`
  min-height: 75px;
  flex-wrap: wrap;
  margin-bottom: 30px;
`;

interface ConnectionWithProviderExampleProps {
  id: string;
  clientName: string;
  clientDescription: string;
  providerFirstName: string;
  providerLastName: string;
  providerSlug: string;
  providerDescription: string;
  providerImageUrl: string;
  providerServices: any[];
  providerSpecializations: any[];
  onProviderConnectionClick: (providerSlug: string) => () => void;
}

const ConnectionWithProviderExample = ({
  id,
  clientName,
  clientDescription,
  providerFirstName,
  providerLastName,
  providerSlug,
  providerDescription,
  providerImageUrl,
  providerServices,
  providerSpecializations,
  onProviderConnectionClick,
}: ConnectionWithProviderExampleProps): JSX.Element => {
  const cuttedProviderSpecializations =
    !!providerSpecializations && !!providerSpecializations.length ? providerSpecializations.slice(0, 2) : [];
  return (
    <Wrapper>
      <UserConnectionPoints>
        <UserConnectionPoint>
          <UserConnectionPointTitle>{clientName}</UserConnectionPointTitle>
          <UserConnectionPointSubtitle>{clientDescription}</UserConnectionPointSubtitle>
        </UserConnectionPoint>
        <UserConnectionPoint>
          <UserConnectionPointTitle>{clientName} found</UserConnectionPointTitle>
          <UserConnectionPointSubtitle id="UserConnectionPointSubtitle">
            {providerDescription}
          </UserConnectionPointSubtitle>
        </UserConnectionPoint>
        <UserConnectionPoint>
          <UserConnectionPointTitle>Specializing in</UserConnectionPointTitle>
          <SpecializationsWrapper>
            {cuttedProviderSpecializations.map(({ id, value }: any) =>
              cuttedProviderSpecializations[cuttedProviderSpecializations.length - 1].id === id ? (
                <UserConnectionPointSubtitle key={id} id="UserConnectionPointSubtitle">
                  {value}
                </UserConnectionPointSubtitle>
              ) : (
                <UserConnectionPointSubtitle key={id} id="UserConnectionPointSubtitle">
                  {value}&nbsp;and&nbsp;
                </UserConnectionPointSubtitle>
              ),
            )}
          </SpecializationsWrapper>
        </UserConnectionPoint>
      </UserConnectionPoints>
      <ProviderBlockWrapper>
        <ProviderBlock>
          <ProviderBlockTitle>{clientName} connected with</ProviderBlockTitle>
          <Flex>
            <ImageWrapper
              data-bg={providerImageUrl}
              onClick={onProviderConnectionClick(providerSlug)}
              className="lazyload"
            />
            <ColumnedFlex>
              <ProviderName onClick={onProviderConnectionClick(providerSlug)}>
                {providerFirstName} {providerLastName}
              </ProviderName>

              <ServicesWrapper>
                {!!providerServices &&
                  !!providerServices.length &&
                  providerServices.map(({ id, value }: any) =>
                    providerServices[providerServices.length - 1].id === id ? (
                      <Title key={id} size="medium">
                        {value}
                      </Title>
                    ) : (
                      <Title key={id} size="medium">
                        {value},&nbsp;
                      </Title>
                    ),
                  )}
              </ServicesWrapper>
            </ColumnedFlex>
          </Flex>
        </ProviderBlock>
      </ProviderBlockWrapper>
    </Wrapper>
  );
};

export default ConnectionWithProviderExample;
